import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Edit, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PetFormHeader } from "../components/PetFormHeader";
import {
  getPetVaccination,
  createVaccinationRecord,
  updateVaccinationRecord,
  deleteVaccinationRecord,
} from "../services/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { validateDate, convertFromISO } from "../utils/dateValidation";
import DatePickerField from "../components/DatePickerField";
import { useTranslation } from 'react-i18next';
import { set } from "lodash";

const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const PetVaccination = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [isEditMode, setIsEditMode] = useState(false);
  const vaccinationFormRef = useRef(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingVaccinationId, setDeletingVaccinationId] = useState(null);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const { petId } = useParams();
  const location = useLocation();
  const [petData, setPetData] = useState(location.state?.petData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vaccinationRecords, setVaccinationRecords] = useState([]);
  const [showVaccinationForm, setShowVaccinationForm] = useState(false);
  const [editingVaccinationId, setEditingVaccinationId] = useState(null);
  const [vaccinationForm, setVaccinationForm] = useState({
    vaccineDate: "",
    vaccineName: "",
    vaccineNumber: "",
    vaccineTimes: "",
    vaccinePosition: "",
  });
  const [dateErrors, setDateErrors] = useState({});

  // Sorting state
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedRecords, setSortedRecords] = useState([]);

  const vaccineOptions = [
    {
      name: t('ZoetisVaccine1'),
      image: "/icons/vaccine1.png",
    },
    {
      name: t('ZoetisVaccine2'),
      image: "/icons/vaccine2.png",
    },
    {
      name: t('ZoetisVaccine3'),
      image: "/icons/vaccine3.png",
    },
    {
      name: t('ZoetisVaccine4'),
      image: "/icons/vaccine4.png",
    },
    {
      name: t('CoreCombinationVaccine1'),
    },
    {
      name: t('RabiesVaccine2'),
    },
    {
      name: t('KennelCoughVaccine3'),
    },
    {
      name: t('MyxoRHDVaccine4'),
    },
    {
      name: t('TricatTrioVaccine5'),
    },
    {
      name: t('PurevaxRCPVaccine6'),
    },
    {
      name: t('DHPPiL4Vaccine7'),
    },
    {
      name: t('LymeDiseaseVaccine8'),
    },
    {
      name: t('FeLVVaccine9'),
    },
    {
      name: t('RHDV2Vaccine10'),
    },
    {
      name: t('RabiesVaccine11'),
    },
  ];

  const injectionNumbers = [
    { label: t('First'), value: "第一針" },
    { label: t('Second'), value: "第二針" },
    { label: t('Third'), value: "第三針" },
    { label: t('Fourth'), value: "第四針" }
  ];
  const injectionSites = [
    { label: t('LeftFrontLeg'), value: "左前腳" },
    { label: t('LeftHindLeg'), value: "左後腳" },
    { label: t('RightFrontLeg'), value: "右前腳" },
    { label: t('RightHindLeg'), value: "右後腳" },
    { label: t('Tail'), value: "尾巴" }
  ];

  const getInjectionNumberLabel = (value) => {
    const injectionNumber = injectionNumbers.find(i => i.value === value);
    return injectionNumber ? injectionNumber.label : value;
  };

  const getInjectionSiteLabel = (value) => {
    const injectionSite = injectionSites.find(i => i.value === value);
    return injectionSite ? injectionSite.label : value;
  };

  // Sorting function
  const sortRecordsByDate = (records, order) => {
    return [...records].sort((a, b) => {
      const dateA = new Date(convertFromISO(a.vaccineDate));
      const dateB = new Date(convertFromISO(b.vaccineDate));

      if (order === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Handle sorting
  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    const sorted = sortRecordsByDate(vaccinationRecords, newOrder);
    setSortedRecords(sorted);
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petData) {
      setIsEditMode(true);
      setVaccinationRecords(petData?.vaccineRecords || []);
    }
  }, [location, petData]);

  // Update sorted records when vaccinationRecords or sortOrder changes
  useEffect(() => {
    const sorted = sortRecordsByDate(vaccinationRecords, sortOrder);
    setSortedRecords(sorted);
  }, [vaccinationRecords, sortOrder]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setVaccinationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewRecord = () => {
    setShowVaccinationForm(true);
    setEditingVaccinationId(null);
    setVaccinationForm({
      vaccineDate: "",
      vaccineName: "",
      vaccineNumber: "",
      vaccineTimes: "",
      vaccinePosition: "",
    });
  };

  const handleEditRecord = (record) => {
    const vaccineIndex = vaccineOptions.findIndex(
      (option) => option.name === record.vaccineName
    );
    setShowVaccinationForm(true);
    setEditingVaccinationId(record._id);
    setVaccinationForm({
      vaccineDate: convertFromISO(record.vaccineDate),
      vaccineName: vaccineIndex >= 0 ? vaccineIndex : "",
      vaccineNumber: record.vaccineNumber,
      vaccineTimes: record.vaccineTimes,
      vaccinePosition: record.vaccinePosition,
    });
    setTimeout(() => {
      vaccinationFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelForm = () => {
    setShowVaccinationForm(false);
    setEditingVaccinationId(null);
    setVaccinationForm({
      vaccineDate: "",
      vaccineName: "",
      vaccineNumber: "",
      vaccineTimes: "",
      vaccinePosition: "",
    });
    setDateErrors({});
  };

  const handleSubmitForm = async () => {
    setButtonLoading(true);
    try {
      // Validate date
      if (vaccinationForm.vaccineDate) {
        const dateValidation = validateDate(vaccinationForm.vaccineDate);
        if (!dateValidation.isValid) {
          setButtonLoading(false);
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          setDateErrors({ vaccineDate: dateValidation.error });
          return;
        }
      }

      const vaccinationData = {
        vaccineDate: vaccinationForm.vaccineDate,
        vaccineName: vaccinationForm.vaccineName,
        vaccineNumber: vaccinationForm.vaccineNumber,
        vaccineTimes: vaccinationForm.vaccineTimes,
        vaccinePosition: vaccinationForm.vaccinePosition,
      };

      if (editingVaccinationId) {
        // Update existing record
        await updateVaccinationRecord(
          petId,
          editingVaccinationId,
          vaccinationData
        );
      } else {
        // Create new record
        await createVaccinationRecord(petId, vaccinationData);
      }

      // Refresh data
      const response = await getPetVaccination(petId);
      setVaccinationRecords(response.data.form.vaccineRecords);
      setPetData((petData) => ({
        ...petData,
        vaccineRecords: response.data.form.vaccineRecords,
      }));
      setButtonLoading(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
      // Reset form
      handleCancelForm();
    } catch (error) {
      setButtonLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving vaccination record:", error);
    }
  };

  const handleDeleteRecord = async () => {
    setDeleteLoading(true);
    try {
      await deleteVaccinationRecord(petId, deletingVaccinationId);

      // Refresh data
      const response = await getPetVaccination(petId);
      setVaccinationRecords(response.data.form.vaccineRecords);
      setPetData((petData) => ({
        ...(petData || {}),
        vaccineRecords: response.data.form.vaccineRecords,
      }));
      setShowVaccinationForm(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error deleting vaccination record:", error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        <PetFormHeader
          currentPageId="vaccination"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        <Box component="form">
          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t('VaccineRecords')}
            <Button
              onClick={handleAddNewRecord}
              disabled={showVaccinationForm}
              sx={{
                ml: 2,
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              variant="outlined"
              color="text.secondary"
            >
              + {t('AddNewRecord')}
            </Button>
          </Typography>
          {!showVaccinationForm && petData?.vaccineRecords?.length === 0 && (
            <Typography fontWeight={700} sx={{ color: "#969696", fontSize: 18, p: 3, display: "flex", justifyContent: "center"}}>{t("noVaccineRecords")}</Typography>
          )}
          {/* Vaccination Records Table */}
          {vaccinationRecords.length > 0 && (
            <Box sx={{ px: 3, mb: 4 }}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { backgroundColor: "#e8e8e8" },
                          }}
                          onClick={handleSort}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {t('InocculationDate')}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                ml: 0.5,
                              }}
                            >
                              {sortOrder === "asc" ? (
                                <ArrowUp size={16} />
                              ) : (
                                <ArrowDown size={16} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('vaccineName')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('vaccineNumber')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('vaccineTimes')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('vaccinePosition')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {convertFromISO(record.vaccineDate)}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {(() => {
                                const vaccineOption = vaccineOptions[record.vaccineName];
                                return vaccineOption && vaccineOption.image ? (
                                  <img
                                    src={vaccineOption.image}
                                    alt={vaccineOption.name}
                                    style={{
                                      width: 40,
                                      height: 25,
                                      objectFit: "cover",
                                      borderRadius: 4,
                                      border: "none",
                                      outline: "none",
                                    }}
                                    onError={(e) => {
                                      console.error(
                                        "Table image failed:",
                                        e.target.src
                                      );
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : null;
                              })()}
                              <Typography variant="body2">
                                {vaccineOptions[record.vaccineName]?.name || "-"}{" "}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{record.vaccineNumber}</TableCell>
                          <TableCell>{getInjectionNumberLabel(record.vaccineTimes)}</TableCell>
                          <TableCell>{getInjectionSiteLabel(record.vaccinePosition)}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleEditRecord(record)}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => { setDeletingVaccinationId(record._id); setShowDeleteModal(true); }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, px: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#666" }}
                  >
                    {t('sortby')}
                  </Typography>
                  <Button
                    onClick={handleSort}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "#FFB60C",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgba(255,182,12,0.08)" },
                    }}
                  >
                    {t('InocculationDate')} {sortOrder === "asc" ? "▲" : "▼"}
                  </Button>
                </Box>
                {sortedRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        {t('VaccineRecords')}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditRecord(record)}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={() => { setDeletingVaccinationId(record._id); setShowDeleteModal(true); }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('InocculationDate')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {convertFromISO(record.vaccineDate) || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccineName')}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {(() => {
                            const vaccineOption = vaccineOptions[record.vaccineName];
                            return vaccineOption && vaccineOption.image ? (
                              <img
                                src={vaccineOption.image}
                                alt={vaccineOption.name}
                                style={{
                                  width: 40,
                                  height: 25,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: "none",
                                  outline: "none",
                                }}
                                onError={(e) => {
                                  console.error(
                                    "Mobile image failed:",
                                    e.target.src
                                  );
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : null;
                          })()}
                          <Typography variant="body1" sx={{ color: "#333" }}>
                            {vaccineOptions[record.vaccineName]?.name || "-"}{" "}
                          </Typography>
                        </Box>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccineNumber')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.vaccineNumber || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccineTimes')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {getInjectionNumberLabel(record.vaccineTimes) || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccinePosition')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {getInjectionSiteLabel(record.vaccinePosition) || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Vaccination Form */}
          {showVaccinationForm && (
            <>
              <Grid
                ref={vaccinationFormRef}
                container
                spacing={3}
                justifyContent="flex-start"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3}}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="vaccineDate">{t('InocculationDate')}</Label>
                  <DatePickerField
                    id="vaccineDate"
                    name="vaccineDate"
                    placeholder="DD/MM/YYYY"
                    value={vaccinationForm.vaccineDate}
                    onChange={(value) =>
                      setVaccinationForm((prev) => ({
                        ...prev,
                        vaccineDate: value,
                      }))
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, vaccineDate: error })
                    }
                    error={!!dateErrors.vaccineDate}
                    helperText={dateErrors.vaccineDate}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="vaccineName">{t('vaccineName')}</Label>
                  <Select
                    id="vaccineName"
                    name="vaccineName"
                    value={vaccinationForm.vaccineName}
                    onChange={(e) =>
                      setVaccinationForm((prev) => ({
                        ...prev,
                        vaccineName: parseInt(e.target.value, 10), // Convert to number
                      }))
                    }
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="textSecondary">
                            {t('PleaseSelect')}
                          </Typography>
                        );
                      }
                      const selectedOption = vaccineOptions[selected];
                      return selectedOption ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {selectedOption.image && (
                            <img
                              src={selectedOption.image}
                              alt={selectedOption.name}
                              style={{
                                width: 40,
                                height: 25,
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "none",
                                outline: "none",
                              }}
                              onError={(e) => {
                                console.error(
                                  "Image failed to load:",
                                  e.target.src
                                );
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <Typography>{selectedOption.name}</Typography>
                        </Box>
                      ) : (
                        selected
                      );
                    }}
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t('PleaseSelect')}
                    </MenuItem>
                    {vaccineOptions.map((option, index) => (
                      <MenuItem key={index} value={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 1,
                          }}
                        >
                          {option.image && (
                            <img
                              src={option.image}
                              alt={option.name}
                              style={{
                                width: 60,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #e0e0e0",
                                outline: "none",
                              }}
                              onError={(e) => {
                                console.error(
                                  "Dropdown image failed:",
                                  e.target.src
                                );
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <Typography>{option.name}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="vaccineNumber">{t('vaccineNumber')}</Label>
                  <TextField
                    id="vaccineNumber"
                    name="vaccineNumber"
                    placeholder={t('vaccineNumber')}
                    value={vaccinationForm.vaccineNumber}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ px: 3, backgroundColor: "#FFF2E2", }}>
                <Label>{t('vaccineTimes')}</Label>
                <RadioGroup
                  row={!isMobile}
                  name="vaccineTimes"
                  value={vaccinationForm.vaccineTimes}
                  onChange={handleFormChange}
                  sx={{ gap: isMobile ? 1 : 4 }}
                >
                  {injectionNumbers.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  ))}
                </RadioGroup>
              </Box>

              <Box sx={{ px: 3,backgroundColor: "#FFF2E2", py: 3 }}>
                <Label>{t('vaccinePosition')}</Label>
                <RadioGroup 
                  row={!isMobile}
                  name="vaccinePosition"
                  value={vaccinationForm.vaccinePosition}
                  onChange={handleFormChange}
                  sx={{ gap: isMobile ? 1 : 4 }}
                >
                  {injectionSites.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  ))}
                  <FormControlLabel
                    value="其他"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {t('other')}：
                        <TextField
                          value={
                            vaccinationForm.vaccinePosition &&
                              !injectionSites.some(site => site.value === vaccinationForm.vaccinePosition)
                              ? vaccinationForm.vaccinePosition
                              : ""
                          }
                          onChange={(e) =>
                            setVaccinationForm((prev) => ({
                              ...prev,
                              vaccinePosition: e.target.value,
                            }))
                          }
                          placeholder=""
                          size="small"
                          sx={{ ml: 1, width: 100, backgroundColor: "white" }}
                        />
                      </Box>
                    }
                    sx={{ mr: 0, mb: isMobile ? 1 : 0 }}
                  />
                </RadioGroup>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 2,
                  px: 3,
                  backgroundColor: "#FFF2E2", pb: 3
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={buttonLoading}
                  sx={{
                    backgroundColor: "#FFB60C",
                    color: "#050505",
                    minWidth: 120,
                    "&:hover": { backgroundColor: "#FFB60C" },
                    "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                    cursor: buttonLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onClick={handleSubmitForm}
                >
                  {t('Save')}
                  {buttonLoading && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CircularProgress size={15} color="inherit" />
                    </span>
                  )}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                  onClick={handleCancelForm}
                >
                  {t('cancel')}
                </Button>
              </Box>
            </>
          )}

          {/* Bottom Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 5, md: 10 },
              mr: 3,
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t('BackToHome')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {t('ConfirmDeleteVaccination')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleDeleteRecord}
          >
            {t('Delete')}
            {deleteLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetVaccination;
